// @ts-check
/**
 * @typedef {import('./types/organisationAiReport.types.js').AiReportGetResponse} AiReportGetResponse
 * @typedef {import('./types/organisationAiReport.types.js').AiReportBody} AiReportBody
 * @typedef {import('./types/organisationAiReport.types.js').AiReportPostResponse} AiReportPostResponse
 */
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import axios from 'axios';

import { useAuthenticatedQuery } from './useAuthenticatedQuery';
import useAuthenticatedMutation from './admin/useAuthenticatedMutation';
import { useOrgContext } from '@datahooks/admin/organisation/AdminOrgIDContext.jsx';
import useUser from '@datahooks/useUser';

import { API_BASE_URL, FUNCTION_KEY, PROJECT_SCOPE } from '../config';
import logError from './metadata/ErrorLogging';

/** The report is generated in the background, so the panel polls for the result. */
export const POLL_INTERVAL_MS = 2000;	// 2 seconds between polls

/** Polling stops here and the panel says so, rather than spinning forever. */
export const POLL_TIMEOUT_MS = 90 * 1000;	// 90 seconds

/**
 * The AI Executive Summary for the current organisation, and the action that generates it.
 *
 * GET never generates, so opening the panel is free. The caller decides when to POST - on a first open
 * with no report, or when the user asks for a refresh - and this hook polls until it resolves.
 *
 * @param {{ enabled?: boolean }} [options] - enabled false while the panel is closed, so it stops polling.
 * @returns {{
 *   status: import('./types/organisationAiReport.types.js').AiReportStatus,
 *   report: AiReportBody | null,
 *   generatedAt: string | null,
 *   isStale: boolean,
 *   changedProjectCount: number,
 *   accessibleProjectCount: number,
 *   statusMessage: string | null,
 *   isLoading: boolean,
 *   isGenerating: boolean,
 *   hasTimedOut: boolean,
 *   error: any,
 *   generateError: any,
 *   generate: (options?: { force?: boolean }) => Promise<AiReportPostResponse>,
 *   refetch: () => Promise<any>,
 *   organisationId: number | null
 * }}
 */
export default function useOrganisationAiReport({ enabled = true } = {}) {
  const { organisationData } = useOrgContext();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [hasTimedOut, setHasTimedOut] = useState(false);

  const organisationId = organisationData?.id ?? null;
  const userId = user?.id ?? null;
  const projectScope = [PROJECT_SCOPE];

  // Includes the user: reports are per user as well as per organisation, because project access varies.
  const queryKey = ['organisationAiReport', organisationId, userId];

  const fetchReport = async (token) => {
    const requestParams = organisationId ? { org_id: organisationId } : {};

    try {
      const response = await axios.get(`${API_BASE_URL}organisation_ai_report`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-functions-key': FUNCTION_KEY,
          'Content-Type': 'application/json',
        },
        params: requestParams,
      });

      return response.data;
    } catch (error) {
      logError('Failed to load the AI report in useOrganisationAiReport', error, requestParams);
      throw error;
    }
  };

  const requestGeneration = async ({ force = false } = {}, token) => {
    const requestBody = { ...(organisationId ? { org_id: organisationId } : {}), force };

    try {
      const response = await axios.post(`${API_BASE_URL}organisation_ai_report`, requestBody, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-functions-key': FUNCTION_KEY,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      logError('Failed to request an AI report in useOrganisationAiReport', error, requestBody);
      throw error;
    }
  };

  const { data, isLoading, refetch, error } = useAuthenticatedQuery(queryKey, fetchReport, projectScope, {
    enabled: enabled && !!organisationId,
    // No caching: the panel polls for a live status, and a stale read would show a finished report as pending.
    staleTime: 0,
    refetchInterval: (/** @type {AiReportGetResponse|undefined} */ latest) => (
      !hasTimedOut && latest?.status === 'pending' ? POLL_INTERVAL_MS : false
    ),
  });

  /** @type {AiReportGetResponse|undefined} */
  const report = data;
  const isRunning = report?.status === 'pending';

  // Timed from when a generation is first seen, so the ceiling covers the whole wait, not each poll.
  useEffect(() => {
    if (!isRunning || !enabled) {
      return undefined;
    }

    const timer = setTimeout(() => setHasTimedOut(true), POLL_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isRunning, enabled]);

  const { mutateAsync, isLoading: isGenerating, error: generateError } = useAuthenticatedMutation(
    requestGeneration,
    projectScope,
    {
      // The POST answers with a status, not a report, so the next read is what shows the result.
      onSuccess: () => queryClient.invalidateQueries(queryKey),
    }
  );

  const generate = useCallback((options = {}) => {
    setHasTimedOut(false);

    return mutateAsync(options);
  }, [mutateAsync]);

  return {
    status: report?.status ?? 'none',
    report: report?.report ?? null,
    generatedAt: report?.generatedAt ?? null,
    isStale: report?.isStale ?? false,
    changedProjectCount: report?.changedProjectCount ?? 0,
    accessibleProjectCount: report?.accessibleProjectCount ?? 0,
    statusMessage: report?.statusMessage ?? null,
    isLoading,
    isGenerating,
    hasTimedOut,
    error,
    generateError,
    generate,
    refetch,
    organisationId,
  };
}
